"use client"

import React, {useEffect} from "react";
import {LinkPreviewItem} from "./LinkPreviewItem";
import {useLinkPreview} from "@/hooks/queries/use-link-preview";
import {getExternalLinkKey} from "@/lib/link-preview-utils";
import type {ExternalLink, ExternalLinkPreview} from "@/pb/api/devplan/types/attachments";

/**
 * Props for the LinkPreviewList component.
 */
export interface LinkPreviewListProps {
  /** The company ID for API authorization */
  companyId: number;
  /** Array of detected external links */
  links: ExternalLink[];
  /** Callback when a link preview is removed */
  onRemove: (link: ExternalLink) => void;
  /** Callback when a preview payload is successfully fetched */
  onPreviewLoaded?: (preview: ExternalLinkPreview, originalLink: ExternalLink) => void;
}

/**
 * Props for the generic preview component.
 */
interface PreviewWithDataProps {
  companyId: number;
  link: ExternalLink;
  onRemove: () => void;
  onPreviewLoaded?: (preview: ExternalLinkPreview, originalLink: ExternalLink) => void;
}

/**
 * Generic preview component that fetches and displays link preview data.
 * Works with any provider that has a backend implementation.
 */
function PreviewWithData({
  companyId,
  link,
  onRemove,
  onPreviewLoaded,
}: PreviewWithDataProps) {
  const {preview, isLoading} = useLinkPreview({
    companyId,
    link,
    enabled: true,
  });

  useEffect(() => {
    if (!preview || !onPreviewLoaded) {
      return;
    }
    onPreviewLoaded(preview, link);
  }, [link, onPreviewLoaded, preview]);

  if (isLoading) {
    return (
      <LinkPreviewItem
        provider={link.provider}
        identifier={link.identifier}
        url={link.url}
        onRemove={onRemove}
        isLoading={true}
      />
    );
  }

  if (!preview) {
    return (
      <LinkPreviewItem
        provider={link.provider}
        identifier={link.identifier}
        url={link.url}
        onRemove={onRemove}
        isLoading={false}
      />
    );
  }

  return (
    <LinkPreviewItem
      provider={link.provider}
      identifier={preview.link?.identifier || link.identifier}
      title={preview.title}
      status={preview.status}
      url={preview.link?.url || link.url}
      onRemove={onRemove}
      isLoading={false}
    />
  );
}

/**
 * A list of link previews for display in the composition area.
 * Supports multiple providers with a single generic data fetching component.
 * Styled consistently with FilePreviewList.
 */
export function LinkPreviewList({
  companyId,
  links,
  onRemove,
  onPreviewLoaded,
}: LinkPreviewListProps) {
  if (links.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {links.map((link) => (
        <PreviewWithData
          key={getExternalLinkKey(link)}
          companyId={companyId}
          link={link}
          onRemove={() => onRemove(link)}
          onPreviewLoaded={onPreviewLoaded}
        />
      ))}
    </div>
  );
}
